"use client"

import { useState, useCallback, useRef } from "react"
import type { ThinkingStep } from "@/components/thinking/types"
import type { TransparencyEvent } from "@/lib/transparency/types"

export function useThinkingState() {
  const [isThinking, setIsThinking] = useState(false)
  const [steps, setSteps] = useState<ThinkingStep[]>([])
  const stepIdMap = useRef<Map<string, string>>(new Map())
  const stepIdCounter = useRef(0)
  const currentRequestId = useRef<string | null>(null)

  const reset = useCallback(() => {
    setIsThinking(false)
    setSteps([])
    stepIdMap.current.clear()
    stepIdCounter.current = 0
    currentRequestId.current = crypto.randomUUID()
  }, [])

  const applyTransparencyEvent = useCallback((event: TransparencyEvent) => {
    console.log("[v0] useThinkingState.applyTransparencyEvent called:", {
      eventType: event.type,
      currentStepsCount: steps.length,
      isThinking,
    })

    // Ignore events from previous requests
    if (!currentRequestId.current) {
      currentRequestId.current = crypto.randomUUID()
    }

    const eventType = event.type

    if (eventType === "thinking_started") {
      console.log("[v0] Setting isThinking=true, clearing steps")
      setIsThinking(true)
      setSteps([])
      stepIdMap.current.clear()
    } else if (eventType === "retrieval_started") {
      const id = `step-${++stepIdCounter.current}`
      stepIdMap.current.set("retrieval", id)
      console.log("[v0] Adding retrieval step:", { id, label: event.display.label })
      setSteps((prev) => [
        ...prev,
        {
          id,
          type: "search",
          status: "active",
          label: event.display.label,
        },
      ])
    } else if (eventType === "retrieval_complete") {
      const stepId = stepIdMap.current.get("retrieval")
      console.log("[v0] Completing retrieval step:", { stepId, hasStepId: !!stepId })
      if (stepId) {
        const data = event.data as any
        setSteps((prev) =>
          prev.map((step) =>
            step.id === stepId
              ? {
                  ...step,
                  status: "complete" as const,
                  metadata: {
                    resultCount: data.results_count || 0,
                    duration: event.duration_ms,
                    items: data.items || [],
                  },
                }
              : step,
          ),
        )
      }
    } else if (eventType === "thinking_complete") {
      console.log("[v0] Setting isThinking=false")
      setIsThinking(false)
    } else if (eventType === "generation_started") {
      const id = `step-${++stepIdCounter.current}`
      stepIdMap.current.set("generation", id)
      console.log("[v0] Adding generation step:", { id, label: event.display.label })
      setSteps((prev) => [
        ...prev,
        {
          id,
          type: "generate",
          status: "active",
          label: event.display.label,
        },
      ])
    } else if (eventType === "generation_complete") {
      const stepId = stepIdMap.current.get("generation")
      if (stepId) {
        setSteps((prev) =>
          prev.map((step) =>
            step.id === stepId
              ? {
                  ...step,
                  status: "complete" as const,
                  metadata: {
                    duration: event.duration_ms,
                  },
                }
              : step,
          ),
        )
      }
    } else if (eventType === "memory_extraction_started") {
      const id = `step-${++stepIdCounter.current}`
      stepIdMap.current.set("memory_extract", id)
      setSteps((prev) => [
        ...prev,
        {
          id,
          type: "extract",
          status: "active",
          label: "Extracting memories...",
        },
      ])
    } else if (eventType === "memory_auto_saved") {
      const stepId = stepIdMap.current.get("memory_extract")
      if (stepId) {
        const data = event.data as any
        setSteps((prev) =>
          prev.map((step) =>
            step.id === stepId
              ? {
                  ...step,
                  status: "complete" as const,
                  label: `Saved: ${data.label || "New memory"}`,
                }
              : step,
          ),
        )
      }
    }
  }, [])

  return {
    isThinking,
    steps,
    applyTransparencyEvent,
    reset,
  }
}
